// CategoryPage.js — Radiance Listing Pages (React/JavaScript version)
// -----------------------------------------------------------------
// Design updates applied per request:
// 1) ❌ Removed Compare option (checkbox, state, and sticky bar component).
// 2) ❌ Removed Concern facet and all related filtering/state.
// 3) ✅ Added a clear header/title above the 3 Top Picks section.

import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, ChevronLeft, ShieldCheck, Search, Flame, ExternalLink, Star, Award, TrendingUp, Beaker, Eye } from 'lucide-react';
import Navbar from '../../components/navbar/Navbar';
import SimilarProducts from '../../components/SimilarProducts';
import { useData } from '../../context/DataContext';
import { scrollToProductAnchor } from '../../utils/scrollUtils';
import { generateProductSlug, generateBrandedProductSlug } from '../../utils/slugUtils';

// -----------------------------------------------------------------
// Utils
// -----------------------------------------------------------------
const formatPrice = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : undefined);
const formatRange = (r) => (r ? `$${r.min.toFixed(0)}–$${r.max.toFixed(0)}` : undefined);
const formatCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

// Get hero image based on category name
function getHeroImageForCategory(categoryName) {
  const normalizedName = categoryName.toLowerCase();
  
  // Hair category gets the hair care essentials image
  if (normalizedName.includes('hair')) {
    return '/images/Hair Care Essentials Flat Lay.png';
  }
  
  // Makeup categories get the makeup swatches image
  if (normalizedName.includes('makeup')) {
    return '/images/makeup-swatches.png';
  }
  
  // Default skincare image
  return '/images/skincare-woman.png';
}

// -----------------------------------------------------------------
// HeaderHero - Editorial style inspired by Byrdie/Wirecutter
// -----------------------------------------------------------------
const HeaderHero = ({ title, subtitle, heroImageUrl, breadcrumbs = [], categoryName }) => {
  return (
    <header className="relative w-full bg-[#f8f8f8] overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c73215' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>
      
      <div className="relative mx-auto max-w-7xl px-4 py-6 sm:py-8 md:py-12">
        {breadcrumbs.length > 0 && (
          <nav className="mb-3 sm:mb-4 text-xs sm:text-sm text-gray-600" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-1 sm:gap-2">
              {breadcrumbs.map((crumb, i) => (
                <li key={i} className="flex items-center gap-1 sm:gap-2">
                  <span className="hover:text-[#ab3d22] transition-colors">{crumb}</span>
                  {i < breadcrumbs.length - 1 && <ChevronRight className="h-3 w-3 text-gray-400" />}
                </li>
              ))}
            </ol>
          </nav>
        )}
        
        <div className={`grid ${heroImageUrl ? 'lg:grid-cols-12' : 'lg:grid-cols-1'} gap-6 sm:gap-8 items-start mt-4`}>
          <div className={heroImageUrl ? "lg:col-span-8" : "lg:col-span-1"}>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-3" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
              {categoryName === 'Skincare' ? 'Skincare' : 
               categoryName === 'Makeup' ? 'Makeup' :
               categoryName === 'Hair' ? 'Hair' :
               categoryName}
            </h1>
            <div className="text-sm sm:text-base md:text-lg text-gray-700 mb-3 sm:mb-4 leading-relaxed">
              {subtitle.split('\n').map((line, index) => (
                <p key={index} className={index > 0 ? 'mt-3 sm:mt-4' : ''}>
                  {line}
                </p>
              ))}
            </div>
            {/* Radiance Trust Ribbon */}
            <div className="mt-4 sm:mt-6 py-3 sm:py-4">
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 lg:gap-8 text-xs sm:text-sm">
                {categoryName === 'Skincare' ? (
                  <>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Verified reviews</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Independent curation</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Always up to date</span>
                    </div>
                  </>
                ) : categoryName === 'Hair' ? (
                  <>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Verified reviews</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Independent curation</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Always up to date</span>
                    </div>
                  </>
                ) : categoryName === 'Makeup' ? (
                  <>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Verified reviews</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Independent curation</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Always up to date</span>
                    </div>
                  </>
                ) : categoryName === 'Body' ? (
                  <>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Verified reviews</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Independent curation</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Always up to date</span>
                    </div>
                  </>
                ) : categoryName === 'Wellness' ? (
                  <>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Verified reviews</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Independent curation</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Always up to date</span>
                    </div>
                  </>
                ) : categoryName === 'Health' ? (
                  <>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Verified reviews</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Independent curation</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Always up to date</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Verified reviews</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Independent curation</span>
                    </div>
                    <div className="text-gray-400 hidden sm:block">|</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#ab3d22]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">Always up to date</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Last Updated - Separate Line with Pill Design */}
            <div className="flex items-center mt-4 sm:mt-6">
              <div className="inline-flex items-center gap-1 sm:gap-2 bg-gray-100 text-gray-600 px-2 sm:px-2.5 py-1 rounded-full text-xs">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
          </div>
          
          {heroImageUrl && (
            <div className="lg:col-span-4 relative flex items-center justify-center mt-6 lg:mt-8 h-64 sm:h-80 overflow-hidden rounded-xl">
              <img 
                src={heroImageUrl} 
                alt={`Best ${categoryName} products`}
                className={`w-full h-full object-cover object-center shadow-lg ${categoryName === 'Hair' ? 'scale-125' : ''}`}
                style={{borderRadius: '0.75rem'}}
              />
              {/* Pinkish gradient overlay for Radiance branding */}
              <div className="absolute inset-0 bg-gradient-to-br from-rose-200/20 via-rose-100/10 to-amber-200/15 rounded-xl"></div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// -----------------------------------------------------------------
// QuickFilters Component
// -----------------------------------------------------------------
const QuickFilters = ({ state, facets, categorySlug, navigate }) => {
  // Helper function to convert subcategory name to URL slug
  const createSubcategorySlug = (skinType) => {
    return skinType.toLowerCase().replace(/\s+/g, '-');
  };

  // Show contextual quick filters based on current selections
  const showSkinTypeFilters = !state.skin && facets?.skinTypes;
  const showProductTypeFilters = state.skin && !state.types && facets?.productTypes;
  
  if (!showSkinTypeFilters && !showProductTypeFilters) {
    return null;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 pb-3">
      {showSkinTypeFilters && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Choose your skin type:
          </p>
          <div className="flex flex-wrap gap-2">
            {facets.skinTypes.map((skinType) => (
              <button
                key={skinType}
                onClick={() => {
                  const subcategorySlug = createSubcategorySlug(skinType);
                  navigate(`/category/${categorySlug}/${subcategorySlug}`);
                }}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-800 hover:bg-[#ab3d22] hover:text-white transition-colors duration-200"
              >
                {skinType}
              </button>
            ))}
          </div>
        </div>
      )}

      {showProductTypeFilters && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Find specific products:
          </p>
          <div className="flex flex-wrap gap-2">
            {facets.productTypes.map((productType) => (
              <button
                key={productType}
                onClick={() => {
                  const productTypeSlug = productType.toLowerCase().replace(/\s+/g, '-');
                  navigate(`/category/${categorySlug}/${state.skin.toLowerCase().replace(/\s+/g, '-')}/${productTypeSlug}`);
                }}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-800 hover:bg-[#ab3d22] hover:text-white transition-colors duration-200"
              >
                {productType}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


const FacetMulti = ({ label, options, selected, onToggle }) => {
  return (
    <div className="relative">
      <details className="group">
        <summary className="cursor-pointer select-none rounded-xl border border-neutral-300 px-3 py-2 text-sm hover:bg-neutral-50">{label}</summary>
        <div className="absolute right-0 mt-2 w-56 rounded-xl border border-neutral-200 bg-[#f8f8f8] p-2 shadow-lg">
          <ul className="max-h-60 overflow-auto text-sm">
            {options.map((opt) => {
              const active = selected.includes(opt);
              return (
                <li key={opt}>
                  <button
                    className={`flex w-full items-center justify-between rounded-lg px-2 py-2 hover:bg-neutral-50 ${active ? 'text-[#ab3d22]' : ''}`}
                    onClick={() => onToggle(opt)}
                  >
                    <span>{opt}</span>
                    {active ? <span aria-hidden>✔</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </details>
    </div>
  );
};

const FacetSingle = ({ label, options, selected, onSelect }) => {
  const handleSelect = (value) => {
    onSelect(value);
    // Close the dropdown after selection
    const details = document.querySelector(`details[data-label="${label}"]`);
    if (details) {
      details.removeAttribute('open');
    }
  };

  // Display text: show selected value or default label
  const displayText = selected ? selected : label;
  const isActive = Boolean(selected);

  return (
    <div className="relative">
      <details className="group" data-label={label}>
        <summary className={`cursor-pointer select-none rounded-xl border px-3 py-2 text-sm ${
          isActive 
            ? 'border-[#ab3d22] bg-[#ab3d22] text-white hover:bg-[#8a2f1a]' 
            : 'border-neutral-300 bg-[#f8f8f8] text-gray-700 hover:text-[#ab3d22] hover:border-[#ab3d22]'
        }`}>
          {displayText}
        </summary>
        <div className="absolute right-0 mt-2 w-56 rounded-xl border border-neutral-200 bg-[#f8f8f8] p-2 shadow-lg">
          <ul className="max-h-60 overflow-auto text-sm">
            <li>
              <button
                className={`flex w-full items-center justify-between rounded-lg px-2 py-2 hover:bg-neutral-50 ${!selected ? 'text-[#ab3d22]' : ''}`}
                onClick={() => handleSelect('')}
              >
                <span>All</span>
                {!selected ? <span aria-hidden>✔</span> : null}
              </button>
            </li>
            {options.map((opt) => {
              const active = selected === opt;
              return (
                <li key={opt}>
                  <button
                    className={`flex w-full items-center justify-between rounded-lg px-2 py-2 hover:bg-neutral-50 ${active ? 'text-[#ab3d22]' : ''}`}
                    onClick={() => handleSelect(opt)}
                  >
                    <span>{opt}</span>
                    {active ? <span aria-hidden>✔</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </details>
    </div>
  );
};

function toggleArray(onChange, key, arr, value) {
  const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
  onChange({ [key]: next });
}

// -----------------------------------------------------------------
// FeaturedPicks Carousel (supports up to 6 products)
// -----------------------------------------------------------------
const FeaturedPicks = ({ picks, categoryName }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Generate up to 6 featured items
  const items = [
    picks.bestOverall && { label: 'Best Overall', p: picks.bestOverall },
    picks.bestValue && { label: 'Best Value', p: picks.bestValue },
    picks.upgradePick && { label: 'Upgrade Pick', p: picks.upgradePick },
    picks.mostPopular && { label: 'Most Popular', p: picks.mostPopular },
    picks.budgetPick && { label: 'Budget Pick', p: picks.budgetPick },
    picks.luxuryPick && { label: 'Luxury Pick', p: picks.luxuryPick }
  ].filter(Boolean);

  if (items.length === 0) return null;

  // Show 3 cards at a time on desktop, 1 on mobile
  const itemsPerSlide = 3;
  const maxSlide = Math.max(0, Math.ceil(items.length / itemsPerSlide) - 1);
  
  const nextSlide = () => {
    setCurrentSlide(prev => prev < maxSlide ? prev + 1 : 0);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => prev > 0 ? prev - 1 : maxSlide);
  };

  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
  };

  return (
    <section aria-label="Featured picks">
      <div className="mb-4 sm:mb-6 text-center">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>Our Top Picks</h2>
        <p className="text-gray-600 mt-2 text-sm sm:text-base px-2">
          {categoryName === 'Skincare' ? 'Best skincare products for every skin type - from cleansers to anti-aging serums' :
           categoryName === 'Hair' ? 'Top-rated hair care products for damaged, dry, oily, and curly hair types' :
           categoryName === 'Makeup' ? 'Best makeup products and cosmetics for long-lasting, professional results' :
           categoryName === 'Body' ? 'Luxurious body care essentials - from moisturizers to targeted treatments for smooth, healthy skin' :
           categoryName === 'Wellness' ? 'Science-backed wellness products - from supplements to self-care essentials for total well-being' :
           categoryName === 'Health' ? 'Clinically-proven health solutions - from immune support to digestive wellness for optimal health' :
           'Top-rated products recommended by beauty experts and real customers'}
        </p>
      </div>
      
      <div className="relative">
        {/* Carousel Container */}
        <div className="overflow-hidden rounded-xl sm:rounded-2xl">
          <div 
            className="flex transition-transform duration-300 ease-in-out"
            style={{ 
              transform: `translateX(-${currentSlide * 100}%)`,
            }}
          >
            {Array.from({ length: Math.ceil(items.length / itemsPerSlide) }).map((_, slideIndex) => (
              <div key={slideIndex} className="w-full flex-shrink-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                  {items.slice(slideIndex * itemsPerSlide, (slideIndex + 1) * itemsPerSlide).map(({ label, p }) => (
                    <article key={label} className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm transition hover:shadow-md h-full flex flex-col">
                      {/* Image Container - Fixed Height */}
                      <div className="flex-shrink-0 mb-3 bg-white rounded-xl p-3 border border-gray-100">
                        <img src={p.imageUrl} alt="" className="h-40 w-full object-contain" />
                      </div>
                      
                      {/* Product Title - Fixed Height */}
                      <div className="flex-shrink-0 mb-2 h-12 flex flex-col justify-start">
                        <h3 className="text-base font-semibold text-neutral-900 line-clamp-2 leading-tight">
                          <span className="tracking-wide uppercase text-neutral-500 mr-1 text-xs block">{p.brand}</span>
                          {p.name}
                        </h3>
                      </div>
                      
                      {/* Description - Flexible Height */}
                      <div className="flex-grow mb-3">
                        <p className="text-sm text-neutral-700 line-clamp-3 leading-relaxed">{p.shortDescription}</p>
                      </div>
                      
                      {/* Rating - Fixed Height */}
                      <div className="flex-shrink-0 mb-3 h-5 flex items-center justify-between text-sm text-neutral-700">
                        <div className="flex items-center gap-1">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <svg key={i} className="w-3 h-3 text-[#ab3d22] fill-current" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                              </svg>
                            ))}
                          </div>
                          <span className="ml-1">{p.rating.toFixed(1)}</span>
                        </div>
                        {/* TEMPORARILY HIDDEN - Price needs updating */}
                        {/* <span className="font-medium">{p.price ? formatPrice(p.price) : p.priceRange ? `${p.priceRange.min}–${p.priceRange.max}` : ''}</span> */}
                      </div>
                      
                      {/* CTA Button - Fixed Height, Always at Bottom */}
                      <div className="flex-shrink-0">
                        <a 
                          href={p.affiliates?.[0]?.url || p.affiliateLink || '#'} 
                          target="_blank" 
                          rel="nofollow noopener sponsored" 
                          className="inline-flex w-full items-center justify-center rounded-xl bg-[#ab3d22] px-4 py-2 text-sm font-medium text-white hover:bg-[#8a2f1a] hover:shadow-md transition-all duration-200 h-10"
                          data-cta="shop_now"
                          data-product={generateBrandedProductSlug(p.brand, p.name)}
                          data-category={categoryName?.toLowerCase().replace(/\s+/g, '-') || ''}
                        >
                          Shop Now
                        </a>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows - only show if more than 3 items */}
        {items.length > 3 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-[#f8f8f8]/90 hover:bg-[#f8f8f8] rounded-full p-2 shadow-lg transition-all duration-200 z-10"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-[#f8f8f8]/90 hover:bg-[#f8f8f8] rounded-full p-2 shadow-lg transition-all duration-200 z-10"
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </>
        )}

        {/* Slide Indicators - only show if more than 1 slide */}
        {maxSlide > 0 && (
          <div className="flex justify-end mt-6 gap-2">
            {Array.from({ length: maxSlide + 1 }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === currentSlide ? 'bg-[#ab3d22]' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// -----------------------------------------------------------------
// ProductRow (Compare option removed) - Enhanced with expandable content
// -----------------------------------------------------------------
  // Old ProductDetailCard component removed - using enhanced version below
  const OldProductDetailCard_REMOVED = ({ product }) => {
    const [expandedSections, setExpandedSections] = useState({
      highlights: false,
      proscons: false,
      ingredients: false,
      radianceTake: false
    });

    const toggleSection = (section) => {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    return (
      <div id={`details-${product.id}`} className="bg-white rounded-2xl border-2 border-[#ab3d22] shadow-lg overflow-hidden relative">
        {/* Product Badges - Top Right Corner */}
        {product.badges && product.badges.length > 0 && (
          <div className="absolute top-4 right-4 z-10">
            <div className="flex flex-wrap gap-1 justify-end">
              {product.badges.map((badge) => (
                <span key={badge} className="inline-flex items-center rounded-full bg-custom-100/10 text-custom-100 border border-custom-100/30 px-3 py-1 text-xs font-medium">
                  {badgeLabel(badge)}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Product Header */}
        <div className="bg-white p-8 border-b border-gray-200">
          <div className="flex items-center gap-6">
            {/* Product Image */}
            <div className="flex-shrink-0">
              <img 
                src={product.imageUrl} 
                alt={product.name} 
                className="w-24 h-24 rounded-xl object-contain"
              />
            </div>
            
            {/* Product Info */}
            <div className="flex-1">
              {/* Brand and Product Name */}
              <div className="mb-3">
                <h4 className="text-sm font-medium text-gray-600 uppercase tracking-wide mb-1">
                  {product.brand}
                </h4>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                  {product.name.replace(product.brand, '').trim()}
                </h3>
              </div>
              
              {/* Product Description */}
              {product.description && (
                <div className="mb-3">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {product.description}
                  </p>
                </div>
              )}
              
              {/* Rating Stars */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-[#ab3d22] fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  ))}
                </div>
                {/* Review count temporarily hidden - will bring back later */}
                <span className="text-sm text-gray-600">
                  {product.rating ? Number(product.rating).toFixed(1) : '4.8'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Product Specs Grid */}
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Price</h4>
              <p className="text-lg font-bold text-gray-900">${product.price}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Skin Type</h4>
              <p className="text-gray-900">{product.suitability?.[0] || 'All Skin Types'}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Key Ingredient</h4>
              <p className="text-gray-900">{
                typeof product.keyIngredients?.[0] === 'string' 
                  ? product.keyIngredients[0]
                  : product.keyIngredients?.[0]?.name 
                  ? product.keyIngredients[0].name
                  : typeof product.keyIngredients?.[0] === 'object' && product.keyIngredients[0]._id 
                  ? Object.values(product.keyIngredients[0]).filter(v => v !== product.keyIngredients[0]._id).join('')
                  : 'Premium Formula'
              }</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Benefits</h4>
              <p className="text-gray-900">{product.benefits?.slice(0, 2).join(', ') || 'Daily Use'}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Fragrance</h4>
              <p className="text-gray-900">{
                (() => {
                  // Handle both array and string formats for backward compatibility
                  let fragranceValue = 'Unscented';
                  if (Array.isArray(product.fragrance) && product.fragrance.length > 0) {
                    fragranceValue = product.fragrance[0];
                  } else if (typeof product.fragrance === 'string' && product.fragrance.trim()) {
                    fragranceValue = product.fragrance;
                  } else if (product.attributes?.fragrance) {
                    fragranceValue = product.attributes.fragrance;
                  }
                  
                  console.log('Product fragrance debug:', {
                    productId: product._id,
                    productName: product.name,
                    productFragrance: product.fragrance,
                    productAttributes: product.attributes,
                    finalValue: fragranceValue
                  });
                  return fragranceValue;
                })()
              }</p>
            </div>
            <div></div>
            <div className="md:col-span-1 lg:col-span-1">
              <a 
                href={product.affiliates?.[0]?.url || product.affiliateLink || '#'} 
                target="_blank" 
                rel="nofollow noopener sponsored" 
                className="inline-flex items-center justify-center w-full bg-[#ab3d22] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#8a2f1a] hover:shadow-md transition-all duration-200"
                data-cta="shop_now"
                data-product={generateBrandedProductSlug(product.brand, product.name)}
                data-category=""
              >
                Shop Now
              </a>
            </div>
          </div>

          {/* Expandable Sections */}
          <div className="space-y-4">
            {/* Highlights Section */}
            <div className="border border-gray-300 rounded-xl overflow-hidden bg-white">
              <button
                onClick={() => toggleSection('highlights')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#ab3d22]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
                  </svg>
                  <h4 className="font-semibold text-[#ab3d22]">Highlights</h4>
                </div>
                <span className={`transform transition-transform text-[#ab3d22] ${expandedSections.highlights ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>
              {expandedSections.highlights && (
                <div className="p-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {product.highlights && product.highlights.length > 0 ? (
                      product.highlights.map((highlight, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-[#ab3d22] rounded-full"></span>
                          <span className="text-sm text-gray-700">{highlight}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">No highlights available</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Pros & Cons Section */}
            <div className="border border-gray-300 rounded-xl overflow-hidden bg-white">
              <button
                onClick={() => toggleSection('proscons')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#ab3d22]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <h4 className="font-semibold text-[#ab3d22]">Pros & Cons</h4>
                </div>
                <span className={`transform transition-transform text-[#ab3d22] ${expandedSections.proscons ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>
              {expandedSections.proscons && (
                <div className="p-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <h5 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                        <span className="text-green-500">✓</span>
                        What we love
                      </h5>
                      <ul className="space-y-2">
                        {product.pros?.map((pro, index) => (
                          <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-green-500 mt-1">•</span>
                            {pro}
                          </li>
                        )) || <p className="text-gray-500 text-sm">No pros listed</p>}
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                        <span className="text-red-500">✗</span>
                        Keep in mind
                      </h5>
                      <ul className="space-y-2">
                        {product.cons?.map((con, index) => (
                          <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-red-500 mt-1">•</span>
                            {con}
                          </li>
                        )) || <p className="text-gray-500 text-sm">No cons listed</p>}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Ingredient Breakdown Section */}
            <div className="border border-gray-300 rounded-xl overflow-hidden bg-white">
              <button
                onClick={() => toggleSection('ingredients')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#ab3d22]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 7.172V5L8 4z"></path>
                  </svg>
                  <h4 className="font-semibold text-[#ab3d22]">Ingredient Breakdown</h4>
                </div>
                <span className={`transform transition-transform text-[#ab3d22] ${expandedSections.ingredients ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>
              {expandedSections.ingredients && (
                <div className="p-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {product.keyIngredients?.map((ingredient, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        <span className="text-sm text-gray-700">{
                          typeof ingredient === 'string' 
                            ? ingredient 
                            : ingredient.name 
                            ? ingredient.name
                            : typeof ingredient === 'object' && ingredient._id 
                            ? Object.values(ingredient).filter(v => v !== ingredient._id).join('')
                            : 'Unknown ingredient'
                        }</span>
                      </div>
                    )) || <p className="text-gray-500">No ingredient information available</p>}
                  </div>
                </div>
              )}
            </div>

            {/* Radiance Take Section */}
            <div className="border border-gray-300 rounded-xl overflow-hidden bg-white">
              <button
                onClick={() => toggleSection('radianceTake')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#ab3d22]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
                  </svg>
                  <h4 className="font-semibold text-[#ab3d22]">Radiance Take</h4>
                </div>
                <span className={`transform transition-transform text-[#ab3d22] ${expandedSections.radianceTake ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>
              {expandedSections.radianceTake && (
                <div className="p-4 border-t border-gray-200">
                  <p className="text-gray-700 leading-relaxed mt-4">
                    {product.radianceTake || `Our beauty experts have thoroughly tested ${product.name} and found it to be an excellent choice for ${product.tags?.[0]?.toLowerCase() || 'daily skincare'}. The formula delivers consistent results with minimal irritation, making it suitable for most skin types. We particularly appreciate its ${
                      typeof product.keyIngredients?.[0] === 'string' 
                        ? product.keyIngredients[0].toLowerCase()
                        : product.keyIngredients?.[0]?.name 
                        ? product.keyIngredients[0].name.toLowerCase()
                        : typeof product.keyIngredients?.[0] === 'object' && product.keyIngredients[0]._id 
                        ? Object.values(product.keyIngredients[0]).filter(v => v !== product.keyIngredients[0]._id).join('').toLowerCase()
                        : 'premium ingredients'
                    } which provide both immediate and long-term benefits.`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ProductRow = ({ product, selectedSubcategory, setState }) => {

  // Check if product has any expandable content
  const hasExpandableContent = (
    (product.pros && product.pros.length > 0) ||
    (product.cons && product.cons.length > 0) ||
    (product.keyIngredients && product.keyIngredients.length > 0) ||
    (product.suitability && product.suitability.length > 0) ||
    (product.attributes && product.attributes.length > 0)
  );

  // Render star rating
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <span key={`full-${i}`} className="text-[#ab3d22]">★</span>
      );
    }

    // Half star
    if (hasHalfStar) {
      stars.push(
        <span key="half" className="text-[#ab3d22]">☆</span>
      );
    }

    // Empty stars
    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <span key={`empty-${i}`} className="text-gray-300">☆</span>
      );
    }

    return stars;
  };

  return (
    <article 
      id={`product-${product.id}`}
      className="rounded-2xl border border-gray-300 bg-white shadow-md hover:shadow-lg transition-all duration-300"
    >
      {/* Main Product Row */}
      <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_200px_200px] items-center gap-6 md:gap-8 p-6">
        <div className="bg-white rounded-xl p-3 mx-auto md:mx-0">
          <img src={product.imageUrl} alt="" className="h-32 w-32 md:h-40 md:w-40 object-contain" />
        </div>

        <div className="min-w-0">
          {/* Subcategory Navigation */}
          {product.tags && product.tags[0] && (
            <div className="mb-1 text-xs">
              <button 
                className="text-[#ab3d22] font-medium hover:underline hover:text-[#8a2f1a] transition-colors cursor-pointer"
                onClick={() => {
                  const tagToFilter = typeof product.tags[0] === 'string' ? product.tags[0] : product.tags[0]?.name || product.tags[0];
                  console.log('Clicked tag in product table:', tagToFilter);
                  setState(prev => ({ 
                    ...prev, 
                    skin: prev.skin === tagToFilter ? '' : tagToFilter,
                    types: '' // Reset types when changing skin filter
                  }));
                  // Scroll to the filter section
                setTimeout(() => {
                  const filterSection = document.getElementById('all-products');
                  if (filterSection) {
                    const elementPosition = filterSection.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - 80;
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                  }
                }, 100);
                }}
              >
                {typeof product.tags[0] === 'string' ? product.tags[0] : product.tags[0]?.name || product.tags[0]}
              </button>
            </div>
          )}
          
          
          <h4 className="text-lg font-semibold text-neutral-900">
            <span className="mr-2 text-sm uppercase tracking-wide text-neutral-500">{product.brand}</span>
            {product.name}
          </h4>
          
          <p className="mt-2 line-clamp-2 text-base text-neutral-700">{product.shortDescription || product.description}</p>
          
          {/* Badges - Important Trust Signals */}
          {product.badges && product.badges.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {product.badges.map((b) => (
                <span key={b} className="inline-flex items-center rounded-full bg-custom-100/10 text-custom-100 border border-custom-100/30 px-2 py-0.5 text-xs font-medium">
                  {badgeLabel(b)}
                </span>
              ))}
            </div>
          )}

          {/* Expand/Collapse Button */}
        </div>

        {/* Rating Section - More spacious */}
        <div className="text-center px-4 md:px-6 py-3 md:py-4 bg-gray-50/50 rounded-xl border border-gray-100">
          <div className="flex items-center justify-center gap-1 mb-2">
            {renderStars(product.rating)}
          </div>
          <div className="text-lg font-semibold text-neutral-900 mb-1">{product.rating.toFixed(1)}</div>
          {/* Review count temporarily hidden - will bring back later */}
          {/* <div className="text-xs text-neutral-600">({formatCount(product.reviewCount)})</div> */}
        </div>

        {/* Price and CTA Section - Optimized spacing */}
        <div className="flex flex-col gap-5 pl-0 md:pl-4 border-l-0 md:border-l border-gray-200">
          {/* TEMPORARILY HIDDEN - Price needs updating */}
          {/* <div className="text-center">
            <div className="text-xl font-bold text-neutral-900 mb-1">
              {product.price ? (
                <span>{formatPrice(product.price)}</span>
              ) : product.priceRange ? (
                <span>{formatRange(product.priceRange)}</span>
              ) : (
                <span>${Math.floor(Math.random() * 80 + 20)}</span>
              )}
            </div>
            
          </div> */}
          
          {/* Dual CTA Strategy */}
          <div className="flex flex-col gap-3">
            {/* Primary CTA - Shop Now */}
            <a 
              href={product.affiliates?.[0]?.url || product.affiliateLink || '#'} 
              target="_blank" 
              rel="nofollow noopener sponsored" 
              className="inline-flex items-center justify-center rounded-xl bg-[#ab3d22] hover:bg-[#8a2f1a] px-5 py-3.5 text-sm font-medium text-white hover:shadow-md transition-all duration-200 shadow-sm"
              data-cta="shop_now"
              data-product={generateBrandedProductSlug(product.brand, product.name)}
              data-category=""
            >
              Shop Now
            </a>
            
            {/* Secondary CTA - Expert Analysis */}
            <Link
              to={`/product/${generateBrandedProductSlug(product.brand, product.name)}`}
              className="group inline-flex items-center justify-center gap-2 rounded-xl border-2 border-gray-300 text-gray-700 hover:bg-black hover:border-black hover:text-white px-4 py-2.5 text-sm font-medium transition-all duration-200"
            >
              <span>See Full Reviews</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Expandable Content Section - Sephora-inspired Highlights */}
      {false && (
        <div className="border-t border-gray-100 px-8 py-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            
            {/* Main Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-12">
              
              {/* Pros Column */}
              <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <h6 className="font-semibold text-black">Pros</h6>
                </div>
                <div className="w-full">
                  {product.pros && product.pros.length > 0 ? (
                    <ul className="space-y-2 text-left">
                      {product.pros.map((pro, index) => (
                        <li key={index} className="text-sm text-black leading-relaxed">
                          • {pro}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 text-center">No pros listed</p>
                  )}
                </div>
              </div>

              {/* Cons Column */}
              <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  <h6 className="font-semibold text-black">Cons</h6>
                </div>
                <div className="w-full">
                  {product.cons && product.cons.length > 0 ? (
                    <ul className="space-y-2 text-left">
                      {product.cons.map((con, index) => (
                        <li key={index} className="text-sm text-red-700 leading-relaxed">
                          • {con}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 text-center">No cons listed</p>
                  )}
                </div>
              </div>

              {/* Key Ingredients Column */}
              <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
                  </svg>
                  <h6 className="font-semibold text-black">Key Ingredients</h6>
                </div>
                <div className="w-full">
                  {product.keyIngredients && product.keyIngredients.length > 0 ? (
                    <ul className="space-y-2 text-left">
                      {product.keyIngredients.slice(0, 5).map((ingredient, index) => (
                        <li key={index} className="text-sm text-black leading-relaxed">
                          • {
                            typeof ingredient === 'string' 
                              ? ingredient 
                              : ingredient.name 
                              ? ingredient.name
                              : typeof ingredient === 'object' && ingredient._id 
                              ? Object.values(ingredient).filter(v => v !== ingredient._id).join('')
                              : 'Unknown ingredient'
                          }
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 text-center">No ingredients listed</p>
                  )}
                </div>
              </div>
              
            </div>

            {/* Secondary Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-8">
              
              {/* Good For Column */}
              <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                  </svg>
                  <h6 className="font-semibold text-black">Good For</h6>
                </div>
                <div className="w-full">
                  {product.suitability && product.suitability.length > 0 ? (
                    <ul className="space-y-2 text-left">
                      {product.suitability.map((item, index) => (
                        <li key={index} className="text-sm text-black leading-relaxed">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 text-center">No suitability info</p>
                  )}
                </div>
              </div>

              {/* Features Column */}
              <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
                  </svg>
                  <h6 className="font-semibold text-black">Features</h6>
                </div>
                <div className="w-full">
                  {(product.attributes && (product.attributes.additional?.length > 0 || product.attributes.fragrance)) || product.fragrance ? (
                    <ul className="space-y-2 text-left">
                      {(() => {
                        const fragranceValue = Array.isArray(product.fragrance) && product.fragrance.length > 0 
                          ? product.fragrance[0] 
                          : product.fragrance || product.attributes?.fragrance;
                        return fragranceValue && (
                          <li className="text-sm text-black leading-relaxed">
                            • Fragrance: {fragranceValue}
                          </li>
                        );
                      })()}
                      {product.attributes.additional?.map((attribute, index) => (
                        <li key={index} className="text-sm text-black leading-relaxed">
                          • {attribute}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 text-center">No features listed</p>
                  )}
                </div>
              </div>
              
            </div>

            {/* Secondary CTA */}
            <div className="text-center pt-4 border-t border-gray-300">
              <a href="#" className="text-sm text-black hover:text-[#ab3d22] transition-colors font-medium">
                Compare with similar products →
              </a>
            </div>

          </div>

          {/* Additional Details */}
          {((product.keyIngredients && product.keyIngredients.length > 3) || 
            (product.suitability && product.suitability.length > 3) || 
            (product.attributes && product.attributes.length > 3)) && (
            <div>
              <h5 className="text-lg font-semibold text-gray-900 mb-4" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                Additional Details
              </h5>
              
              <div className="space-y-4">
                {/* Remaining Key Ingredients */}
                {product.keyIngredients && product.keyIngredients.length > 3 && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-sm">
                    <h6 className="text-sm font-semibold text-gray-900 mb-3">All Key Ingredients</h6>
                    <div className="flex flex-wrap gap-2">
                      {product.keyIngredients.map((ingredient, index) => (
                        <span key={index} className="inline-flex items-center bg-[#fbf6f3] text-[#ab3d22] px-3 py-1 rounded-full text-xs font-medium border border-[#ab3d22]/20">
                          {ingredient}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Remaining Suitability */}
                {product.suitability && product.suitability.length > 3 && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-sm">
                    <h6 className="text-sm font-semibold text-gray-900 mb-3">Complete Suitability</h6>
                    <div className="flex flex-wrap gap-2">
                      {product.suitability.map((item, index) => (
                        <span key={index} className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium border border-green-200">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Remaining Attributes */}
                {((product.attributes && (product.attributes.additional?.length > 0 || product.attributes.fragrance)) || product.fragrance) && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-sm">
                    <h6 className="text-sm font-semibold text-gray-900 mb-3">All Features</h6>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        const fragranceValue = Array.isArray(product.fragrance) && product.fragrance.length > 0 
                          ? product.fragrance[0] 
                          : product.fragrance || product.attributes?.fragrance;
                        return fragranceValue && (
                          <span className="inline-flex items-center bg-gray-50 text-gray-700 px-3 py-1 rounded-full text-xs font-medium border border-gray-200">
                            Fragrance: {fragranceValue}
                          </span>
                        );
                      })()}
                      {product.attributes.additional?.map((attribute, index) => (
                        <span key={index} className="inline-flex items-center bg-gray-50 text-gray-700 px-3 py-1 rounded-full text-xs font-medium border border-gray-200">
                          {attribute}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
};

function badgeLabel(b) {
  switch (b) {
    case 'editors_choice':
      return "Editor's Choice";
    case 'derm_approved':
      return 'Derm Approved';
    case 'best_value':
      return 'Best Value';
    case 'best_seller':
      return 'Best Seller';
    case 'upgrade_pick':
      return 'Upgrade Pick';
    case 'clean_beauty':
      return 'Clean Beauty';
    case 'trending':
      return 'Trending';
    default:
      return b;
  }
}


// -----------------------------------------------------------------
// Homepage Footer Component
// -----------------------------------------------------------------
const Container = ({ children, className = "" }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const HomepageFooter = ({ categories = [] }) => {
  return (
    <footer className="bg-black">
      <Container className="py-12 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* LEFT HALF: Brand + Newsletter */}
          <div className="lg:pr-8">
            <Link 
              to="/" 
              className="text-2xl sm:text-3xl font-bold lowercase mb-6 inline-block hover:opacity-80 transition-opacity"
              style={{ 
                fontFamily: 'Poppins, sans-serif', 
                fontWeight: 700,
                color: '#ffffff'
              }}
            >
              <span style={{ color: '#ffffff' }}>radiance</span><span style={{ color: '#ab3d22' }}>.</span>
            </Link>
            
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Your shortcut to smarter self-care.
            </h3>
            <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
              Honest beauty and wellness guidance, curated picks, and clear reasons why they're worth considering.
            </p>
            
            <a 
              href="http://eepurl.com/jpTPXQ"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#ab3d22] hover:bg-[#8a2f1a] text-white font-semibold rounded-lg transition-colors"
            >
              Subscribe
              <ChevronRight className="h-4 w-4" />
            </a>
            
            <p className="text-gray-500 text-sm mt-4">
              No noise. Just clarity.
            </p>
          </div>

          {/* RIGHT HALF: Links */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 text-sm">
            {/* Explore */}
            <div>
              <div className="font-semibold text-white mb-4">Explore</div>
              <ul className="space-y-2.5 text-gray-400">
                {categories && categories.length > 0 ? (
                  categories.slice(0, 6).map(category => (
                    <li key={category._id}>
                      <Link to={`/category/${category.slug}`} className="hover:text-white transition-colors">
                        {category.name}
                      </Link>
                    </li>
                  ))
                ) : (
                  <>
                    <li><Link to="/category/skincare" className="hover:text-white transition-colors">Skincare</Link></li>
                    <li><Link to="/category/hair" className="hover:text-white transition-colors">Hair</Link></li>
                    <li><Link to="/category/makeup" className="hover:text-white transition-colors">Makeup</Link></li>
                    <li><Link to="/category/body" className="hover:text-white transition-colors">Body</Link></li>
                    <li><Link to="/category/wellness" className="hover:text-white transition-colors">Wellness</Link></li>
                    <li><Link to="/category/health" className="hover:text-white transition-colors">Health</Link></li>
                  </>
                )}
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <div className="font-semibold text-white mb-4">Company</div>
              <ul className="space-y-2.5 text-gray-400">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/editorial-standards" className="hover:text-white transition-colors">Editorial Standards</Link></li>
                <li><Link to="/affiliate-disclosure" className="hover:text-white transition-colors">Affiliate Disclosure</Link></li>
                <li><Link to="/advertise" className="hover:text-white transition-colors">Advertise</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link></li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <div className="font-semibold text-white mb-4">Connect</div>
              <ul className="space-y-2.5 text-gray-400">
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Bottom row */}
        <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
          <p>&copy; 2025 Radiance. All rights reserved.</p>
        </div>
      </Container>
    </footer>
  );
};

// -----------------------------------------------------------------
// Main CategoryPage Component
// -----------------------------------------------------------------
const CategoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const {
    categories,
    subcategories,
    products,
    loading,
    getCategoryBySlug,
    getSubcategoriesForCategory,
    getProductsForCategory,
    getTagsForSubcategory
  } = useData();

  const [category, setCategory] = useState(null);
  const [categorySubcategories, setCategorySubcategories] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categoryTags, setCategoryTags] = useState([]);
  
  const [state, setState] = useState({
    sort: 'top_rated',
    skin: '',
    types: ''
  });

  const [page, setPage] = useState(1);
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter subscription for:', email);
    setEmail('');
  };

  useEffect(() => {
    if (loading) return;

    const currentCategory = getCategoryBySlug(slug);
    if (currentCategory) {
      setCategory(currentCategory);

      // Get subcategories for this category
      const subs = getSubcategoriesForCategory(currentCategory._id);
      setCategorySubcategories(subs);

      // Get all products for this category
      const allCategoryProducts = getProductsForCategory(currentCategory._id);
      setCategoryProducts(allCategoryProducts);

      // Get tags for all subcategories in this category
      const allTags = [];
      subs.forEach(subcategory => {
        const subcategoryTags = getTagsForSubcategory(subcategory._id);
        allTags.push(...subcategoryTags);
      });
      setCategoryTags(allTags);
    }
  }, [loading, slug, categories, subcategories, products, getTagsForSubcategory]);

  // Reset filters to default (ALL) when slug changes (new page/refresh)
  useEffect(() => {
    setState({
      sort: 'top_rated',
      skin: '',
      types: ''
    });
    setPage(1);
  }, [slug]);

  // Handle scrolling to specific product if anchor is present in URL
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#product-')) {
      // Use the scroll utility that accounts for fixed header
      scrollToProductAnchor(hash, 500);
    }
  }, [categoryProducts]); // Trigger when products are loaded

  // Transform products to match expected format
  const transformedProducts = useMemo(() => {
    console.log('=== TRANSFORMING PRODUCTS ===');
    console.log('Total products:', categoryProducts.length);
    return categoryProducts.map((product, index) => {
      // Use actual badges from admin form
      const badges = product.badges || [];

      // Get subcategory name and actual product tags
      const subcategory = categorySubcategories.find(sub =>
        sub._id === product.subcategory ||
        (product.subcategory && product.subcategory._id && sub._id === product.subcategory._id)
      );

      // Build comprehensive tags array for filtering
      const allTags = [];
      
      // 1. Add subcategory name (like "Moisturizers", "Cleansers", etc.)
      if (subcategory) {
        allTags.push(subcategory.name);
      }
      
      // 2. Add product-specific tags from the admin panel
      if (product.tags && Array.isArray(product.tags)) {
        product.tags.forEach(tag => {
          // Handle both populated (object) and unpopulated (string/ID) tags
          let tagName = null;
          
          if (typeof tag === 'object' && tag !== null) {
            // Tag is populated - use the name property
            tagName = tag.name;
          } else if (typeof tag === 'string') {
            // Tag is a string - check if it's an ObjectId or actual name
            // MongoDB ObjectIds are 24 hex characters
            if (!tag.match(/^[0-9a-fA-F]{24}$/)) {
              tagName = tag;
            }
          }
          
          if (tagName && !allTags.includes(tagName)) {
            allTags.push(tagName);
          }
        });
      }

      // Debug log for first few products
      if (index < 3) {
        console.log(`Product ${index + 1}: "${product.name}"`);
        console.log('  - Raw product.tags:', product.tags);
        console.log('  - Final allTags:', allTags);
      }

      return {
        id: product._id,
        brand: product.brand,
        name: product.name,
        imageUrl: product.imageUrl || 'https://images.unsplash.com/photo-1611930022073-b7a4ba5fcccd?w=400',
        tags: allTags,
        shortDescription: product.shortDescription || product.description || 'Premium quality product recommended by our experts.',
        description: product.description,
        rating: product.rating || 4.0,
        reviewCount: product.reviewCount || 0,
        price: product.price || 0,
        badges: badges,
        affiliates: [{ merchant: 'Amazon', url: product.affiliateLink || '#' }],
        affiliateLink: product.affiliateLink,
        // Additional expandable content fields
        highlights: product.highlights || [],
        pros: product.pros || [],
        cons: product.cons || [],
        keyIngredients: product.keyIngredients || [],
        benefits: product.benefits || [],
        suitability: product.suitability || [],
        radianceTake: product.radianceTake || '',
        attributes: (() => {
          // Use separate fragrance and size fields if available, otherwise fall back to attributes array
          let fragrance = '';
          const additional = [];
          
          // Check separate fields first (new format)
          if (product.fragrance && Array.isArray(product.fragrance) && product.fragrance.length > 0) {
            fragrance = product.fragrance[0];
          } else if (typeof product.fragrance === 'string' && product.fragrance.trim()) {
            fragrance = product.fragrance;
          }
          
          
          // If separate fields are not available, check old attributes format
          if (!fragrance) {
            const attrs = product.attributes || [];
            const fragrancePatterns = /^fragrance:/i;
            
            attrs.forEach(attr => {
              if (fragrancePatterns.test(attr) && !fragrance) {
                // Remove "Fragrance: " prefix for display
                fragrance = attr.replace(/^fragrance:\s*/i, '');
              } else {
                additional.push(attr);
              }
            });
          }
          
          return {
            fragrance,
            additional
          };
        })()
      };
    });
  }, [categoryProducts, categorySubcategories, categoryTags]);

  // Featured products logic - up to 6 products
  // ONLY show products marked as "Featured" in admin panel - NO FALLBACK
  const featuredProducts = useMemo(() => {
    // Filter products marked as featured in admin panel
    const featuredInCategory = transformedProducts.filter(p => p.isFeatured);
    
    console.log('🔍 Category Page - Featured Products:', {
      categoryName: category?.name || 'Loading...',
      totalProducts: transformedProducts.length,
      featuredCount: featuredInCategory.length,
      featuredProducts: featuredInCategory.map(p => p.name)
    });
    
    // ONLY show featured products - no fallback to algorithm
    if (featuredInCategory.length === 0) {
      console.warn('⚠️ No featured products found for', category?.name || 'this category', '- "Our Top Picks" section will not display');
      return null; // Return null to hide the section
    }
    
    const sorted = [...featuredInCategory].sort((a, b) => b.rating - a.rating);
    const budgetProducts = sorted.filter(p => p.price && p.price < 25);
    const midRangeProducts = sorted.filter(p => p.price && p.price >= 25 && p.price <= 60);
    const luxuryProducts = sorted.filter(p => p.price && p.price > 60);
    
    return {
      bestOverall: sorted[0],
      bestValue: budgetProducts[0] || sorted.find(p => p.price && p.price < 30) || sorted[1],
      upgradePick: luxuryProducts[0] || sorted.find(p => p.price && p.price > 50) || sorted[2],
      mostPopular: sorted[3] || sorted[1],
      budgetPick: budgetProducts[1] || budgetProducts[0] || sorted.find(p => p.price && p.price < 20) || sorted[4],
      luxuryPick: luxuryProducts[1] || luxuryProducts[0] || sorted.find(p => p.price && p.price > 80) || sorted[5]
    };
  }, [transformedProducts, category?.name]);

  // Filtering and sorting logic
  const filtered = useMemo(() => {
    console.log('=== FILTERING DEBUG ===');
    console.log('state.skin:', state.skin);
    console.log('state.types:', state.types);
    console.log('Total products before filtering:', transformedProducts.length);
    
    const afterSkinFilter = transformedProducts.filter((p) => {
      if (!state.skin) return true;
      const hasTag = p.tags.includes(state.skin);
      if (!hasTag) {
        console.log(`Product "${p.name}" does NOT have skin tag "${state.skin}". Has tags:`, p.tags);
      }
      return hasTag;
    });
    console.log('After skin filter:', afterSkinFilter.length);
    
    const afterTypesFilter = afterSkinFilter.filter((p) => {
      if (!state.types) return true;
      const hasTag = p.tags.includes(state.types);
      if (!hasTag) {
        console.log(`Product "${p.name}" does NOT have type tag "${state.types}". Has tags:`, p.tags);
      }
      return hasTag;
    });
    console.log('After types filter:', afterTypesFilter.length);
    
    return afterTypesFilter
      .sort((a, b) => {
        switch (state.sort) {
          case 'price_asc': {
            const pa = a.price ?? a.priceRange?.min ?? Number.POSITIVE_INFINITY;
            const pb = b.price ?? b.priceRange?.min ?? Number.POSITIVE_INFINITY;
            return pa - pb;
          }
          case 'price_desc': {
            const pa = a.price ?? a.priceRange?.max ?? 0;
            const pb = b.price ?? b.priceRange?.max ?? 0;
            return pb - pa;
          }
          case 'trending':
            // Enhanced trending algorithm: badges + rating + recency
            const aTrendingScore = (a.badges?.includes('trending') ? 100 : 0) + 
                                 (a.badges?.includes('best_seller') ? 50 : 0) + 
                                 (a.rating * 10) + 
                                 (a.reviewCount > 1000 ? 20 : a.reviewCount > 500 ? 10 : a.reviewCount > 100 ? 5 : 0);
            const bTrendingScore = (b.badges?.includes('trending') ? 100 : 0) + 
                                 (b.badges?.includes('best_seller') ? 50 : 0) + 
                                 (b.rating * 10) + 
                                 (b.reviewCount > 1000 ? 20 : b.reviewCount > 500 ? 10 : b.reviewCount > 100 ? 5 : 0);
            return bTrendingScore - aTrendingScore;
          case 'best_value':
            // Enhanced best value: rating-to-price ratio with minimum quality threshold
            const getPrice = (product) => product.price ?? product.priceRange?.min ?? product.priceRange?.max ?? 50;
            const getValueScore = (product) => {
              const price = getPrice(product);
              const rating = product.rating;
              // Only consider products with decent ratings (3.5+) for best value
              if (rating < 3.5) return 0;
              // Value score: rating squared divided by price (higher rating gets exponential boost)
              return (rating * rating) / price * 100;
            };
            const aValueScore = getValueScore(a);
            const bValueScore = getValueScore(b);
            return bValueScore - aValueScore;
          case 'top_rated':
          default:
            // Enhanced top rated: combined score of rating and review count
            // Products with same rating sorted by review count, normalize both to create weighted score
            const aRating = Number(a.rating) || 4.0;
            const bRating = Number(b.rating) || 4.0;
            const aReviews = Number(a.reviewCount) || 0;
            const bReviews = Number(b.reviewCount) || 0;
            
            // Calculate a weighted score: rating carries more weight but reviews break ties
            // Rating score (0-5 -> 0-100) + review score (logarithmic, up to ~30 points)
            const aScore = (aRating * 20) + (Math.log10(aReviews + 1) * 10);
            const bScore = (bRating * 20) + (Math.log10(bReviews + 1) * 10);
            
            if (bScore !== aScore) {
              return bScore - aScore;
            }
            // Final tiebreaker: alphabetical by name for consistency
            return (a.name || '').localeCompare(b.name || '');
        }
      });
  }, [transformedProducts, state]);

  const pageSize = 24;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const start = (page - 1) * pageSize;
  const pageItems = filtered.slice(start, start + pageSize);

  // Create facets from available data
  const facets = useMemo(() => {
    console.log('=== FACETS DEBUG ===');
    console.log('categorySubcategories:', categorySubcategories);
    console.log('transformedProducts count:', transformedProducts.length);
    
    // First level: Show subcategories (like Lotions, Creams, Body Butters, etc.)
    const skinTypes = [...new Set(categorySubcategories.map(sub => sub.name))];
    console.log('Available skinTypes (subcategories):', skinTypes);
    
    // Second level: Product types (tags) only appear after selecting a subcategory
    let productTypes = [];
    if (state.skin) {
      console.log('Selected skin/subcategory:', state.skin);
      // Get products that match the selected subcategory
      const productsWithSelectedSubcategory = transformedProducts.filter(p => 
        p.tags.includes(state.skin)
      );
      console.log('Products matching selected subcategory:', productsWithSelectedSubcategory.length);
      
      if (productsWithSelectedSubcategory.length > 0) {
        console.log('First matching product tags:', productsWithSelectedSubcategory[0]?.tags);
        console.log('Second matching product tags:', productsWithSelectedSubcategory[1]?.tags);
      }
      
      // Extract tags that are product types (not subcategory names)
      const subcategoryNames = categorySubcategories.map(sub => sub.name);
      console.log('Subcategory names to exclude:', subcategoryNames);
      
      // Get all unique tags from matching products, excluding subcategory names
      const allTagsFromProducts = productsWithSelectedSubcategory.flatMap(p => p.tags);
      console.log('All tags from matching products:', allTagsFromProducts);
      
      productTypes = [...new Set(productsWithSelectedSubcategory.flatMap(p => 
        p.tags.filter(tag => 
          !subcategoryNames.includes(tag) && 
          tag !== state.skin // Exclude the currently selected subcategory
        )
      ))];
      console.log('Calculated productTypes (after filtering):', productTypes);
    }
    
    return {
      skinTypes: skinTypes.length > 0 ? skinTypes : ['Lotions', 'Creams', 'Body Butters', 'Oils'],
      productTypes: productTypes.length > 0 ? productTypes : null // Only show if we have types and subcategory is selected
    };
  }, [categorySubcategories, transformedProducts, state.skin]);

  // REMOVED: Loading spinner - page renders instantly with fallback data

  if (!category) {
    return (
      <div className="bg-[#f8f8f8] text-gray-900 min-h-screen">
        <Navbar />
        <div className="container mx-auto px-4 py-24 text-center">
          <h1 className="text-4xl font-bold mb-4">Category not found</h1>
          <p className="text-lg text-gray-600 mb-8">The category you're looking for doesn't exist or has been removed.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-[#ab3d22] text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-[#8a2f1a] transition-colors shadow-sm hover:shadow-md">
            Return to Homepage
            <ChevronRight className="h-5 w-5" />
          </Link>
        </div>
        <HomepageFooter categories={categories} />
      </div>
    );
  }

  const title = `${category.name} — Best Products`;
  // Use dynamic description from admin portal, fallback to default if not available
  const subtitle = category.description || `Discover premium ${category.name.toLowerCase()} products that deliver real results. Our curated selection focuses on quality, performance, and value.`;
  const breadcrumbs = ['Home', category.name];
  
  // Construct full image URL if category has an imageUrl
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl || imageUrl === 'no-image.jpg') return null;
    if (imageUrl.startsWith('http')) return imageUrl; // Already a full URL
    if (imageUrl.startsWith('/images/')) {
      // Construct full URL with API base URL
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      return `${baseURL}${imageUrl}`;
    }
    return imageUrl;
  };
  
  const fullImageUrl = getFullImageUrl(category.imageUrl);

  // Generate dynamic heading for product list
  const generateProductListHeading = () => {
    const selectedSkinType = state.skin;
    const selectedProductType = state.types;
    
    if (!selectedSkinType && !selectedProductType) {
      return `All Products`;
    }
    
    // If only concern is selected, show clean product name
    if (selectedSkinType && !selectedProductType) {
      // Convert concern names to clean product names
      const concernToProductName = {
        'Eyes': 'Eye Products',
        'Lips': 'Lip Products', 
        'Anti-aging': 'Anti-Aging Products',
        'Acne': 'Acne Products',
        'Sun Protection': 'Sun Protection Products',
        'Normal Skin': 'Normal Skin Products',
        'Dry Skin': 'Dry Skin Products',
        'Oily Skin': 'Oily Skin Products',
        'Combination Skin': 'Combination Skin Products',
        'Sensitive Skin': 'Sensitive Skin Products'
      };
      
      return concernToProductName[selectedSkinType] || `${selectedSkinType} Products`;
    }
    
    // If both concern and product type are selected, keep concern context
    if (selectedSkinType && selectedProductType) {
      const concernToProductName = {
        'Eyes': 'Eye Products',
        'Lips': 'Lip Products', 
        'Anti-aging': 'Anti-Aging Products',
        'Acne': 'Acne Products',
        'Sun Protection': 'Sun Protection Products',
        'Normal Skin': 'Normal Skin Products',
        'Dry Skin': 'Dry Skin Products',
        'Oily Skin': 'Oily Skin Products',
        'Combination Skin': 'Combination Skin Products',
        'Sensitive Skin': 'Sensitive Skin Products'
      };
      
      return concernToProductName[selectedSkinType] || `${selectedSkinType} Products`;
    }
    
    // If only product type is selected (shouldn't happen with current UI flow)
    if (selectedProductType) {
      return `${selectedProductType} Products`;
    }
    
    return `All Products`;
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <Navbar />
      
      <HeaderHero 
        title={title} 
        subtitle={subtitle} 
        breadcrumbs={breadcrumbs}
        categoryName={category.name}
        heroImageUrl={fullImageUrl}
      />

      {/* Shop by Concern - Navigation Section */}
      <div className="bg-[#f8f8f8] py-12">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center mb-6">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
              Find Your Perfect Match
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {category.name === 'Skincare' ? 
                'Find products specifically formulated for your skin concerns. From anti-aging to acne treatment, discover targeted solutions for every skin type and need.' :
               category.name === 'Hair' ? 
                'Discover hair care solutions tailored to your specific hair concerns. From damage repair to color protection, find products that address your unique hair challenges.' :
               category.name === 'Makeup' ? 
                'Explore makeup products organized by your beauty concerns. From long-lasting coverage to specific skin tone matches, find the perfect products for your look.' :
               category.name === 'Body' ? 
                'Shop body care products by your specific concerns. From dry skin to cellulite treatment, discover targeted solutions for every part of your body.' :
               category.name === 'Wellness' ? 
                'Find wellness products that address your health and beauty concerns. From stress relief to energy support, discover holistic solutions for total well-being.' :
               category.name === 'Health' ? 
                'Shop health products organized by your specific health concerns. From immune support to digestive health, find science-backed solutions for optimal wellness.' :
                'Find products specifically formulated for your concerns. Discover targeted solutions that address your specific needs and lifestyle.'}
            </p>
          </div>
          
          <div className="flex justify-center">
            <div className="flex flex-wrap gap-3 justify-center">
              {/* All Button - Scrolls to Shop our recommendations section */}
              <button
                onClick={() => {
                  const element = document.getElementById('all-products');
                  if (element) {
                    // Get the element's position and add some offset to account for sticky header
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - 100; // 100px offset from top
                    
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
                className="inline-flex items-center px-6 py-3 rounded-xl text-sm font-medium bg-[#ab3d22] border-2 border-[#ab3d22] text-white hover:bg-[#a82a15] hover:border-[#a82a15] transition-all duration-200 shadow-sm hover:shadow-md"
              >
                All Products
                <ChevronDown className="ml-2 h-4 w-4" />
              </button>
              
              {facets?.skinTypes?.map((skinType) => (
                <button
                  key={skinType}
                  onClick={() => {
                    const subcategorySlug = skinType.toLowerCase().replace(/\s+/g, '-');
                    navigate(`/category/${slug}/${subcategorySlug}`);
                    
                    // Scroll directly to the product table after navigation
                    setTimeout(() => {
                      const productSection = document.getElementById('all-products');
                      if (productSection) {
                        const elementPosition = productSection.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - 100;
                        
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth'
                        });
                      }
                    }, 200);
                  }}
                  className="inline-flex items-center px-6 py-3 rounded-xl text-sm font-medium bg-white border-2 border-gray-300 text-gray-800 hover:border-[#ab3d22] hover:bg-[#ab3d22] hover:text-white transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  {skinType}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Featured Picks - Only show if there are featured products */}
      {featuredProducts && (
        <div className="bg-[#f8f8f8] py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-4">
            <div className="relative">
              <div className="hidden lg:block absolute -inset-4 bg-gradient-to-r from-custom-100/20 to-transparent rounded-3xl"></div>
              <div className="relative bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-300">
                <FeaturedPicks picks={featuredProducts} categoryName={category.name} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shop our recommendations Section - With inline filtering */}
      <div id="all-products" className="bg-[#f8f8f8] py-4">
        <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-4">
          {/* Context Breadcrumb - Show when filtering is active */}
          {state.skin && (
            <div className="flex items-center gap-2 mb-4 text-sm">
              <Link to="/" className="text-gray-500 hover:text-[#ab3d22] transition-colors">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-gray-400" />
              <Link to={`/category/${slug}`} className="text-gray-500 hover:text-[#ab3d22] transition-colors">
                {category.name}
              </Link>
              <ChevronRight className="h-3 w-3 text-gray-400" />
              <span className="text-[#ab3d22] font-medium">{state.skin}</span>
            </div>
          )}
          
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
              Curated Just for You
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find products curated from real reviews and expert recommendations
            </p>
          </div>
        </div>
      </div>

      <div className="bg-[#f8f8f8] pt-1">
        <main className="mx-auto max-w-7xl px-4 pb-16">

          {/* Unified Container - Filters, Products, and Pagination */}
          <div className="bg-white border border-gray-300 rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
              <div className="min-w-0">
                {/* Category Context Badge - Show subcategory if selected */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                  <span className="inline-flex items-center bg-[#ab3d22] text-white text-xs font-semibold px-3 py-1.5 rounded-full uppercase tracking-wide">
                    {state.skin || category.name}
                  </span>
                  {state.skin && (
                    <>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500 uppercase tracking-wide break-words">
                        {category.name} PRODUCTS
                      </span>
                    </>
                  )}
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 break-words">
                  {generateProductListHeading()}
                </h3>
                <p className="text-xs sm:text-sm text-gray-500">Use the filters below to find exactly what you're looking for</p>
              </div>
              <div className="flex flex-col gap-3 sm:gap-4 w-full sm:w-auto">
                {/* Sort By */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <label className="text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">SORT BY</label>
                  <select
                    value={state.sort}
                    onChange={(e) => { setState(prev => ({ ...prev, sort: e.target.value })); setPage(1); }}
                    className="rounded-xl border border-gray-300 bg-white px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm outline-none focus:ring-2 focus:ring-[#ab3d22] focus:border-[#ab3d22] shadow-sm w-full sm:w-auto"
                    aria-label="Sort products"
                  >
                    <option value="top_rated">Top Rated</option>
                    <option value="best_value">Best Value</option>
                    <option value="trending">Trending</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Streamlined filter sections */}
            {facets?.skinTypes && facets.skinTypes.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                  <span className="text-xs sm:text-sm font-semibold text-gray-800 uppercase tracking-wide whitespace-nowrap">Refine your search:</span>
                  <div className="h-px bg-gradient-to-r from-gray-300 to-transparent flex-1 sm:ml-2"></div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setState(prev => ({ ...prev, skin: '', types: '' }));
                      setPage(1);
                    }}
                    className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 ${
                      !state.skin
                        ? 'bg-[#ab3d22] text-white shadow-md hover:shadow-lg'
                        : 'bg-gray-200 text-gray-800 hover:bg-[#ab3d22] hover:text-white hover:shadow-md border border-gray-300'
                    }`}
                  >
                    All
                  </button>
                  {facets.skinTypes.map((skinType) => (
                    <button
                      key={skinType}
                      onClick={() => {
                        console.log('Clicked skinType/subcategory:', skinType);
                        // Reset types filter when changing subcategory
                        setState(prev => ({ 
                          ...prev, 
                          skin: prev.skin === skinType ? '' : skinType,
                          types: '' // Always reset types when changing subcategory
                        }));
                        setPage(1);
                      }}
                      className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 ${
                        state.skin === skinType
                          ? 'bg-[#ab3d22] text-white shadow-md hover:shadow-lg'
                          : 'bg-gray-200 text-gray-800 hover:bg-[#ab3d22] hover:text-white hover:shadow-md border border-gray-300'
                      }`}
                    >
                      {skinType}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Product type refinement - only when concern selected */}
            {state.skin && facets?.productTypes && facets.productTypes.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                  <span className="text-xs sm:text-sm font-semibold text-gray-800 uppercase tracking-wide whitespace-nowrap">Product type:</span>
                  <div className="h-px bg-gradient-to-r from-gray-300 to-transparent flex-1 sm:ml-2"></div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setState(prev => ({ ...prev, types: '' }));
                      setPage(1);
                    }}
                    className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 ${
                      !state.types
                        ? 'bg-[#ab3d22] text-white shadow-md hover:shadow-lg'
                        : 'bg-gray-200 text-gray-800 hover:bg-[#ab3d22] hover:text-white hover:shadow-md border border-gray-300'
                    }`}
                  >
                    All
                  </button>
                  {facets.productTypes.map((productType) => (
                    <button
                      key={productType}
                      onClick={() => {
                        console.log('Clicked productType:', productType);
                        setState(prev => ({ ...prev, types: prev.types === productType ? '' : productType }));
                        setPage(1);
                      }}
                      className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 ${
                        state.types === productType
                          ? 'bg-[#ab3d22] text-white shadow-md hover:shadow-lg'
                          : 'bg-gray-200 text-gray-800 hover:bg-[#ab3d22] hover:text-white hover:shadow-md border border-gray-300'
                      }`}
                    >
                      {productType}
                    </button>
                  ))}
                </div>
              </div>
            )}


            {/* Product Grid */}
            <div className="bg-gray-50 rounded-xl p-6 border-t border-gray-200 mt-6">
              <div className="grid gap-6 mb-2">
                {pageItems.map((p) => (
                  <ProductRow key={p.id} product={p} selectedSubcategory={state.skin} setState={setState} />
                ))}
              </div>
            </div>

            {/* Enhanced Pagination */}
            {filtered.length > 0 && (
              <div className="pt-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Results Info */}
                  <div className="text-sm text-gray-500">
                    Showing <span className="font-medium">{start + 1}-{Math.min(start + pageSize, filtered.length)}</span> of <span className="font-medium">{filtered.length}</span> products
                  </div>
                  
                  {/* Pagination Controls */}
                  {pageCount > 1 && (
                    <div className="flex items-center gap-2">
                      {/* Previous Button */}
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className={`flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                          page === 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:text-[#ab3d22] hover:bg-[#f8f8f8] hover:shadow-md'
                        }`}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="hidden sm:inline">Previous</span>
                      </button>

                      {/* Page Numbers */}
                      <div className="flex items-center gap-1">
                        {(() => {
                          const pages = [];
                          const showPages = 5; // Maximum number of page buttons to show
                          let startPage = Math.max(1, page - Math.floor(showPages / 2));
                          let endPage = Math.min(pageCount, startPage + showPages - 1);
                          
                          // Adjust start if we're near the end
                          if (endPage - startPage + 1 < showPages) {
                            startPage = Math.max(1, endPage - showPages + 1);
                          }

                          // First page + ellipsis if needed
                          if (startPage > 1) {
                            pages.push(
                              <button
                                key={1}
                                onClick={() => setPage(1)}
                                className="min-w-[36px] h-9 rounded-xl border border-neutral-200 bg-[#f8f8f8] hover:bg-neutral-50 text-sm font-medium transition-colors"
                              >
                                1
                              </button>
                            );
                            if (startPage > 2) {
                              pages.push(
                                <span key="ellipsis1" className="px-2 text-gray-400">
                                  ...
                                </span>
                              );
                            }
                          }

                          // Page numbers
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => setPage(i)}
                                className={`min-w-[36px] h-9 rounded-xl border text-sm font-medium transition-all duration-200 ${
                                  i === page
                                    ? 'border-[#ab3d22] bg-[#ab3d22] text-white shadow-md'
                                    : 'border-neutral-200 bg-[#f8f8f8] hover:bg-neutral-50 hover:border-[#ab3d22] hover:text-[#ab3d22]'
                                }`}
                              >
                                {i}
                              </button>
                            );
                          }

                          // Last page + ellipsis if needed
                          if (endPage < pageCount) {
                            if (endPage < pageCount - 1) {
                              pages.push(
                                <span key="ellipsis2" className="px-2 text-gray-400">
                                  ...
                                </span>
                              );
                            }
                            pages.push(
                              <button
                                key={pageCount}
                                onClick={() => setPage(pageCount)}
                                className="min-w-[36px] h-9 rounded-xl border border-neutral-200 bg-[#f8f8f8] hover:bg-neutral-50 text-sm font-medium transition-colors"
                              >
                                {pageCount}
                              </button>
                            );
                          }

                          return pages;
                        })()}
                      </div>

                      {/* Next Button */}
                      <button
                        onClick={() => setPage(Math.min(pageCount, page + 1))}
                        disabled={page === pageCount}
                        className={`flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                          page === pageCount
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:text-[#ab3d22] hover:bg-[#f8f8f8] hover:shadow-md'
                        }`}
                        aria-label="Next page"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {pageItems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">No products found matching your criteria.</p>
              <button 
                onClick={() => setState({ q: '', sort: 'top_rated', skin: '', types: '' })}
                className="mt-4 text-[#ab3d22] hover:underline"
              >
                Clear all filters
              </button>
            </div>
          )}
          
        </main>
      </div>

      {/* Similar Products Carousel */}
      <SimilarProducts 
        products={categoryProducts}
        title="Shop More in This Category"
        subtitle={`Discover other amazing ${category?.name || 'products'} products curated by our experts`}
      />

      <HomepageFooter categories={categories} />
    </div>
  );
};

export default CategoryPage;